/** Worker-exclusive: Welfare Ledger (insurance · medical · grants). */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { HeartHandshake, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function WorkerFeaturePanel({ org, quorum, accent = "#8AA6B8" }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal]     = useState(0);
  const [shownAmount, setShownAmount] = useState(0);
  const [open, setOpen]       = useState(false);
  const unlocked = quorum?.full_features_unlocked;

  const refresh = async () => {
    if (!unlocked) return;
    try {
      const { data } = await api.get(`/orgs/${org.id}/welfare-ledger`, { params: { limit: 20 } });
      setEntries(data.items || []);
      setTotal(data.total || 0);
      setShownAmount(data.shown_amount || 0);
    } catch (err) {
      console.warn("[WorkerFeaturePanel] welfare load skipped:", err?.message);
    }
  };
  useEffect(() => { void refresh(); }, [unlocked, org.id]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="worker-welfare-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: `${accent}20`, color: accent }}>
            <HeartHandshake size={16} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Welfare Ledger</div>
            <div className="font-display text-xl uppercase tracking-tight" data-testid="welfare-total">
              {total.toLocaleString()} entries · ₹{shownAmount.toLocaleString()}
            </div>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!unlocked}
                    className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                    style={unlocked ? { background: accent, color: "#0b0b0d" } : {}}
                    data-testid="issue-welfare-btn">
              <Plus size={12} className="mr-1.5" /> Issue Benefit
            </Button>
          </DialogTrigger>
          <IssueWelfareDialog orgId={org.id} onDone={() => { setOpen(false); refresh(); }} accent={accent} />
        </Dialog>
      </div>
      {!unlocked && (
        <div className="mt-4 flex items-center gap-2 text-[11px] text-amber-300">
          <ShieldAlert size={12} /> Feature unlocks after quorum + KYC.
        </div>
      )}
      {unlocked && entries.length === 0 && (
        <div className="mt-4 text-sm text-white/55">No entries yet. Issue the first benefit above.</div>
      )}
      {unlocked && entries.length > 0 && (
        <ul className="mt-4 divide-y divide-white/5" data-testid="welfare-list">
          {entries.map((e) => (
            <li key={e.id} className="py-2.5 flex items-center gap-3" data-testid={`welfare-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{e.member_name}</div>
                <div className="text-[11px] text-white/55">
                  {e.kind} · {e.membership_number || "—"} · {new Date(e.issued_at).toLocaleDateString()}
                </div>
              </div>
              <div className="text-sm font-mono text-white/80">
                {e.currency} {(e.amount || 0).toLocaleString()}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase ${
                e.status === "active" ? "bg-emerald-500/10 text-emerald-300" :
                e.status === "claimed" ? "bg-blue-500/15 text-blue-300" :
                "bg-white/10 text-white/55"
              }`}>{e.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IssueWelfareDialog({ orgId, onDone, accent }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("medical");
  const [amount, setAmount] = useState("");
  const [memNo, setMemNo] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orgs/${orgId}/welfare-ledger`, {
        member_name: name, kind, amount: parseFloat(amount) || 0,
        membership_number: memNo,
      });
      toast.success("Welfare benefit issued");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not issue");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="issue-welfare-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Issue Welfare Benefit</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Issued entries appear on the member&apos;s dashboard immediately.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Member Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="welfare-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Kind</Label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="mt-2 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-white text-sm"
                    data-testid="welfare-kind-select">
              {["insurance","medical","education","grant","pension","bereavement","wage_supplement","other"].map(k =>
                <option key={k} value={k}>{k.replace("_"," ")}</option>
              )}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Amount (INR) *</Label>
            <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="welfare-amount-input" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Membership #</Label>
          <Input value={memNo} onChange={(e) => setMemNo(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15 font-mono uppercase"
                 data-testid="welfare-memno-input" />
        </div>
        <Button onClick={submit} disabled={!name || !amount || busy}
                className="w-full h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                style={{ background: accent, color: "#0b0b0d" }}
                data-testid="submit-welfare-btn">
          {busy ? "Issuing…" : "Issue Benefit"}
        </Button>
      </div>
    </DialogContent>
  );
}
